/**
 * Vibe editor model. PURE — no React, no I/O.
 *
 * A vibe restyles an action's output and carries its own price (spec D4):
 * background, context, prompt customisation, optional model override, a
 * multiplier that REPLACES the action's base, a seasonal window, a tier
 * feature gate and "announce as NEW" days. Many vibes per action, one default.
 */
import type { AiModelDto } from '../../../services/aiControl/registry';
import type { AiActionDto, AiVibeDto, ModelRefDto } from '../../../services/aiControl/rubyAi';
import {
  DAYS_IN_MONTH, findModel, isPriced, modelHasCapability, modelMatchesGroup, modelRef, parseModelRef,
} from './catalogModel';

/** The two image actions a vibe's "Model for pictures" override applies to. */
export const VIBE_PICTURE_ACTION_IDS = ['image.single_object', 'image.multiple_objects'] as const;

export interface VibeDraft {
  isNew: boolean;
  vibeId: string;
  label: string;
  color: string;
  enabled: boolean;
  background: string;
  context: string;
  promptCustomisation: string;
  /** "providerId#modelId", '' = the action's own model. Covers TEXT actions. */
  model: string;
  /**
   * "providerId#modelId", '' = the action's own model. "Model for pictures"
   * (2026-10-04, Ruby Generation Engine) — writes the SAME override into
   * `modelOverrides` for both VIBE_PICTURE_ACTION_IDS at once.
   */
  modelForPictures: string;
  /** Skips background removal so the vibe's scene stays — media-service reads this. */
  keepScene: boolean;
  /**
   * The vibe's RAW `modelOverrides` map as loaded, untouched by the UI —
   * kept so `buildVibePatch` can merge the picture-override change back in
   * without destroying an override for some OTHER action on the same vibe
   * (the backend PUT replaces the whole field, it does not merge keys).
   */
  modelOverrides: Record<string, ModelRefDto> | undefined;
  /** '' = the action's base multiplier. */
  multiplier: string;
  /** '' = every tier that has the action. */
  featureId: string;
  seasonal: boolean;
  fromMonthDay: string;
  toMonthDay: string;
  announceNew: boolean;
  announceNewDays: string;
  actionIds: string[];
  defaultFor: string[];
}

export const DEFAULT_VIBE_COLOR = '#DC143C';

export function buildVibeDraft(v: AiVibeDto): VibeDraft {
  return {
    isNew: false,
    vibeId: v.vibeId,
    label: v.label ?? '',
    color: v.color ?? DEFAULT_VIBE_COLOR,
    enabled: v.enabled !== false,
    background: v.background ?? '',
    context: v.context ?? '',
    promptCustomisation: v.promptCustomisation ?? '',
    model: modelRef(v.providerId, v.modelId),
    modelForPictures: modelRef(
      v.modelOverrides?.[VIBE_PICTURE_ACTION_IDS[0]]?.providerId,
      v.modelOverrides?.[VIBE_PICTURE_ACTION_IDS[0]]?.modelId,
    ),
    keepScene: !!v.keepScene,
    modelOverrides: v.modelOverrides ? { ...v.modelOverrides } : undefined,
    multiplier: typeof v.multiplier === 'number' ? String(v.multiplier) : '',
    featureId: v.featureId ?? '',
    seasonal: !!v.season,
    fromMonthDay: v.season?.fromMonthDay ?? '11-15',
    toMonthDay: v.season?.toMonthDay ?? '12-31',
    announceNew: !!v.announceNewDays,
    announceNewDays: v.announceNewDays ? String(v.announceNewDays) : '14',
    actionIds: [...(v.actionIds ?? [])],
    defaultFor: [...(v.defaultFor ?? [])],
  };
}

export function newVibeDraft(actionId?: string): VibeDraft {
  return {
    isNew: true,
    vibeId: '',
    label: '',
    color: DEFAULT_VIBE_COLOR,
    enabled: false,
    background: '',
    context: '',
    promptCustomisation: '',
    model: '',
    modelForPictures: '',
    keepScene: false,
    modelOverrides: undefined,
    multiplier: '',
    featureId: '',
    seasonal: false,
    fromMonthDay: '11-15',
    toMonthDay: '12-31',
    announceNew: true,
    announceNewDays: '14',
    actionIds: actionId ? [actionId] : [],
    defaultFor: [],
  };
}

/** "Black Friday!" → "black-friday". */
export function slugifyVibeId(label: string): string {
  return label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

export function isValidMonthDay(md: string): boolean {
  const m = /^(\d{2})-(\d{2})$/.exec(md);
  if (!m) return false;
  const month = Number(m[1]);
  const day = Number(m[2]);
  return month >= 1 && month <= 12 && day >= 1 && day <= DAYS_IN_MONTH[month - 1];
}

export interface VibeIssues { errors: string[]; warnings: string[] }

export function validateVibeDraft(
  d: VibeDraft,
  ctx: { actions: AiActionDto[]; models: AiModelDto[]; vocabulary: readonly string[]; existingIds: string[] },
): VibeIssues {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!d.label.trim()) errors.push('Give the vibe a name merchants will see.');
  if (d.isNew) {
    if (!/^[a-z0-9_-]{1,64}$/.test(d.vibeId)) errors.push('Vibe id must be lowercase letters, numbers, - or _.');
    else if (ctx.existingIds.includes(d.vibeId)) errors.push(`A vibe with id "${d.vibeId}" already exists.`);
  }

  if (d.multiplier.trim() !== '') {
    const n = Number(d.multiplier);
    if (!Number.isFinite(n) || n < 0 || n > 50) errors.push('Multiplier must be a number from 0 to 50, or blank for the action’s base.');
    else if (n < 1) warnings.push('A multiplier below 1 × charges tenants less than the vendor charges us.');
  }

  if (d.seasonal) {
    if (!isValidMonthDay(d.fromMonthDay)) errors.push('"Available from" is not a real date.');
    if (!isValidMonthDay(d.toMonthDay)) errors.push('"Until" is not a real date.');
  }

  if (d.announceNew) {
    const n = Number(d.announceNewDays);
    if (!Number.isInteger(n) || n < 1 || n > 365) errors.push('Announce as NEW needs a whole number of days from 1 to 365.');
  }

  if (d.defaultFor.some((a) => !d.actionIds.includes(a))) {
    errors.push('A vibe can only be the default on an action it is assigned to.');
  }
  if (d.actionIds.length === 0) warnings.push('Not assigned to any action — merchants will never see it.');

  const m = parseModelRef(d.model);
  if (d.model && !m) errors.push('Choose a model or “Action default”.');
  if (m) {
    const model = findModel(ctx.models, m.providerId, m.modelId);
    if (!model) errors.push(`Model ${m.providerId}/${m.modelId} is not in the catalogue.`);
    else {
      for (const aid of d.actionIds) {
        const a = ctx.actions.find((x) => x.actionId === aid);
        if (a && !modelMatchesGroup(model, a.group, ctx.vocabulary)) {
          errors.push(`${model.label} cannot run “${a.label}” — it is not marked for ${a.group}.`);
        }
      }
      if (!isPriced(model)) warnings.push(`${model.label} has no vendor price — an unpriced call cannot be charged.`);
      if (!model.enabled) warnings.push(`${model.label} is disabled — runs will use the action’s fallback.`);
    }
  }

  const pm = parseModelRef(d.modelForPictures);
  if (d.modelForPictures && !pm) errors.push('Choose a model for pictures or “Action default”.');
  if (pm) {
    const model = findModel(ctx.models, pm.providerId, pm.modelId);
    if (!model) errors.push(`Model ${pm.providerId}/${pm.modelId} is not in the catalogue.`);
    else {
      if (!modelHasCapability(model, 'draws_images', ctx.vocabulary)) {
        errors.push(`${model.label} cannot draw pictures — it is not marked "Draws images" on Providers & Models.`);
      }
      if (!isPriced(model)) warnings.push(`${model.label} has no vendor price — an unpriced call cannot be charged.`);
      if (!model.enabled) warnings.push(`${model.label} is disabled — picture runs will use the action’s fallback.`);
    }
  }

  return { errors, warnings };
}

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

export function vibeDraftDirty(o: VibeDraft, d: VibeDraft): boolean {
  if (d.isNew) return true;
  return (Object.keys(d) as Array<keyof VibeDraft>).some((k) => {
    const a = o[k]; const b = d[k];
    return Array.isArray(a) && Array.isArray(b) ? !sameList(a, b) : a !== b;
  });
}

/**
 * Merge patch for PUT /agents/admin/ai/vibes/{id}. Cleared optionals are sent
 * as `null` (JSON drops `undefined`, which would silently keep the old value).
 * A NEW vibe gets the full row and no nulls.
 */
export function buildVibePatch(o: VibeDraft, d: VibeDraft): Record<string, unknown> {
  const full = d.isNew;
  const patch: Record<string, unknown> = {};
  const changed = <K extends keyof VibeDraft>(k: K) => {
    if (full) return true;
    const a = o[k]; const b = d[k];
    return Array.isArray(a) && Array.isArray(b) ? !sameList(a, b) : a !== b;
  };

  if (changed('label')) patch.label = d.label.trim();
  if (changed('color')) patch.color = d.color;
  if (changed('enabled')) patch.enabled = d.enabled;
  if (changed('background')) patch.background = d.background;
  if (changed('context')) patch.context = d.context;
  if (changed('promptCustomisation')) patch.promptCustomisation = d.promptCustomisation;
  if (changed('actionIds')) patch.actionIds = d.actionIds;
  if (changed('defaultFor')) patch.defaultFor = d.defaultFor;

  if (changed('model')) {
    const m = parseModelRef(d.model);
    if (m) { patch.providerId = m.providerId; patch.modelId = m.modelId; }
    else if (!full) { patch.providerId = null; patch.modelId = null; }
  }
  if (changed('modelForPictures')) {
    // The backend PUT replaces the WHOLE `modelOverrides` field — it does not
    // merge keys — so a picture-only change must carry forward every other
    // action's override from the vibe's own raw map, not just the two
    // picture keys, or a text-action override on this same vibe is destroyed.
    const merged: Record<string, ModelRefDto> = { ...(o.modelOverrides ?? {}) };
    const m = parseModelRef(d.modelForPictures);
    if (m) {
      for (const id of VIBE_PICTURE_ACTION_IDS) merged[id] = { providerId: m.providerId, modelId: m.modelId };
      patch.modelOverrides = merged;
    } else if (!full) {
      for (const id of VIBE_PICTURE_ACTION_IDS) delete merged[id];
      patch.modelOverrides = Object.keys(merged).length ? merged : null;
    }
  }
  if (changed('keepScene')) patch.keepScene = d.keepScene;
  if (changed('multiplier')) {
    if (d.multiplier.trim() !== '') patch.multiplier = Number(d.multiplier);
    else if (!full) patch.multiplier = null;
  }
  if (changed('featureId')) {
    if (d.featureId) patch.featureId = d.featureId;
    else if (!full) patch.featureId = null;
  }
  if (changed('seasonal') || changed('fromMonthDay') || changed('toMonthDay')) {
    if (d.seasonal) patch.season = { fromMonthDay: d.fromMonthDay, toMonthDay: d.toMonthDay };
    else if (!full) patch.season = null;
  }
  if (changed('announceNew') || changed('announceNewDays')) {
    if (d.announceNew) patch.announceNewDays = Number(d.announceNewDays);
    else if (!full) patch.announceNewDays = null;
  }
  return patch;
}

/** Models a vibe may override with: those that can run EVERY assigned action. */
export function vibeModelCandidates(
  models: AiModelDto[], actions: AiActionDto[], actionIds: string[], vocabulary: readonly string[],
): AiModelDto[] {
  const assigned = actions.filter((a) => actionIds.includes(a.actionId));
  if (!assigned.length) return models.filter((m) => modelMatchesGroup(m, 'text', vocabulary));
  return models.filter((m) => assigned.every((a) => modelMatchesGroup(m, a.group, vocabulary)));
}
